import type { Club, ClubEvent } from "@/lib/api";
import { formatEventDate, formatEventTime } from "@/lib/format";
import { hasWeeklyMeeting, primaryLink } from "@/lib/meeting";
import { sectionById } from "@/lib/sections";
import { useReveal } from "@/lib/useReveal";
import { SectionHeader } from "./SectionHeader";

interface MeetingsSectionProps {
  readonly club: Club | null;
  readonly events: readonly ClubEvent[];
  readonly loading: boolean;
}

/** The standing slot, or an honest admission that there isn't one yet. */
function ScheduleCard({ club }: { readonly club: Club | null }) {
  const meeting = club?.meeting;
  const link = primaryLink(club);

  if (!hasWeeklyMeeting(club) || !meeting) {
    return (
      <div className="panel offset-ink flex flex-col gap-6 self-start p-6 sm:p-7">
        <div>
          <span className="meta opacity-70">When we meet</span>
          <p className="display mt-2 text-[clamp(2rem,5.5vw,3rem)] leading-[0.95]">
            Posted in Discord
          </p>
        </div>
        <div className="border-t-2 border-ink pt-5">
          <p className="text-[0.9375rem] leading-relaxed">
            This term's schedule is still being arranged. Meeting times are announced in the
            Discord first.
          </p>
          {link ? (
            <a
              className="btn mt-5"
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Join the {link.label}
            </a>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="panel offset-ink flex flex-col gap-6 self-start p-6 sm:p-7">
      <div>
        <span className="meta opacity-70">Every week</span>
        <p className="display mt-2 text-[clamp(2.5rem,7vw,4rem)] leading-[0.9]">
          {meeting.weekday_name}s
        </p>
        <p className="mt-2 font-mono text-2xl">
          {formatEventTime(`2026-01-01T${meeting.start_time}:00`)}
        </p>
      </div>
      {meeting.blurb || meeting.location_note ? (
        <div className="border-t-2 border-ink pt-5">
          {meeting.blurb ? (
            <p className="text-[0.9375rem] leading-relaxed">{meeting.blurb}</p>
          ) : null}
          {meeting.location_note ? (
            <p className="mt-3 text-[0.9375rem] leading-relaxed opacity-75">
              {meeting.location_note}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function EventList({
  events,
  loading,
  club,
}: {
  readonly events: readonly ClubEvent[];
  readonly loading: boolean;
  readonly club: Club | null;
}) {
  if (loading && events.length === 0) {
    return (
      <ol className="flex flex-col gap-3">
        {[0, 1, 2].map((key) => (
          <li key={key} className="panel h-[104px] animate-pulse opacity-40" />
        ))}
      </ol>
    );
  }

  if (events.length === 0) {
    const link = primaryLink(club);
    return (
      <div className="panel flex flex-col justify-center gap-3 p-6 sm:p-7">
        <span className="meta opacity-70">Calendar</span>
        <p className="display text-2xl leading-tight">Nothing on the books yet</p>
        <p className="max-w-[42ch] text-[0.9375rem] leading-relaxed opacity-75">
          Meetings, tournaments and socials appear here once they are scheduled.
          {link ? " The Discord has the latest until then." : ""}
        </p>
      </div>
    );
  }

  return (
    <ol className="flex flex-col gap-3">
      {events.map((event) => {
        const date = formatEventDate(event.starts_at);
        // One-off events carry the S piece's green so they do not read as just
        // another instance of the weekly session.
        const isOneOff = event.kind !== "meeting";
        return (
          <li
            key={event.id}
            className="panel flex items-stretch gap-4 p-5 transition-transform sm:gap-5 sm:p-6
              duration-100 hover:-translate-y-0.5"
          >
            <div
              className="flex w-[74px] shrink-0 flex-col items-center justify-center
                border-2 border-ink py-2"
              style={{
                background: isOneOff
                  ? "color-mix(in srgb, var(--color-piece-s) 30%, transparent)"
                  : "color-mix(in srgb, var(--color-piece-l) 25%, transparent)",
              }}
            >
              <span className="meta text-[0.5625rem]">{date.month}</span>
              <span className="display text-3xl leading-none">{date.day}</span>
              <span className="meta text-[0.5rem] opacity-70">{date.weekday}</span>
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h3 className="text-xl">{event.title}</h3>
                {isOneOff ? (
                  <span
                    className="meta border-2 border-ink px-1.5 py-0.5 text-[0.5rem] leading-none"
                    style={{ background: "var(--color-piece-s)" }}
                  >
                    One-off
                  </span>
                ) : null}
              </div>
              <p className="mt-1 font-mono text-sm">{formatEventTime(event.starts_at)}</p>
              {event.detail ? (
                <p className="mt-1.5 text-sm leading-relaxed opacity-75">{event.detail}</p>
              ) : null}
              {event.location_note ? (
                <p className="mt-1 text-sm leading-relaxed opacity-60">{event.location_note}</p>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export function MeetingsSection({ club, events, loading }: MeetingsSectionProps) {
  const ref = useReveal<HTMLDivElement>();
  const section = sectionById("meetings")!;

  return (
    <section id="meetings" className="band-deep border-y-2 border-ink">
      <div className="shell py-16 sm:py-20 md:py-24">
        <SectionHeader section={section} kicker="Open to anyone. No sign-up required." />

        <div ref={ref} className="reveal grid gap-8 lg:grid-cols-[0.9fr_1.1fr]" data-shown="false">
          <ScheduleCard club={club} />
          <EventList events={events} loading={loading} club={club} />
        </div>
      </div>
    </section>
  );
}
